import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService, ApiRequestOptions } from './api.service';
import { environment } from '../../../environments/environment';
import { Feature, FeatureOption, Product, ProductFeatureMapping, PromptVersion } from '../models/prompt-bot.model';

@Injectable({
    providedIn: 'root'
})
export class PromptBotService {
    private api = inject(ApiService);
    private readonly serverName = environment.ai.productServerName;

    // Default options for Prompt Hub operations
    private readonly defaultOptions: ApiRequestOptions = {
        productServerName: this.serverName,
        showLoader: true,
        showErrorToaster: true
    };

    // Feature Master Library
    getFeatures(options?: ApiRequestOptions): Observable<Feature[]> {
        return this.api.get('/features', {}, { ...this.defaultOptions, ...options });
    }

    createFeature(data: Feature): Observable<Feature> {
        return this.api.post('/feature/add', data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    updateFeature(id: number, data: Feature): Observable<Feature> {
        return this.api.put(`/feature/update/${id}`, data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    deleteFeature(id: number): Observable<any> {
        return this.api.delete(`/feature/delete/${id}`, { ...this.defaultOptions, showSuccessToaster: true });
    }

    // Feature Options Hub
    getFeatureOptions(featureId: number, options?: ApiRequestOptions): Observable<FeatureOption[]> {
        return this.api.get(`/feature-options/${featureId}`, {}, { ...this.defaultOptions, showLoader: false, ...options });
    }

    createFeatureOption(data: FeatureOption): Observable<FeatureOption> {
        return this.api.post('/feature-option/add', data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    updateFeatureOption(id: number, data: FeatureOption): Observable<FeatureOption> {
        return this.api.put(`/feature-option/update/${id}`, data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    deleteFeatureOption(id: number): Observable<any> {
        return this.api.delete(`/feature-option/delete/${id}`, { ...this.defaultOptions, showSuccessToaster: true });
    }

    // Mapping Hub
    getMappings(productId: number, options?: ApiRequestOptions): Observable<ProductFeatureMapping[]> {
        return this.api.get('/product-features', { product_id: productId }, { ...this.defaultOptions, ...options });
    }

    createMapping(data: ProductFeatureMapping): Observable<ProductFeatureMapping> {
        return this.api.post('/product-feature/add', data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    deleteMapping(id: number): Observable<any> {
        return this.api.delete(`/product-feature/delete/${id}`, { ...this.defaultOptions, showSuccessToaster: true });
    }

    // Products List (Master)
    getProducts(options?: ApiRequestOptions): Observable<Product[]> {
        return this.api.get('/products', {}, { ...this.defaultOptions, ...options });
    }

    // Versioned Prompt Manager
    getPrompts(productId?: number, featureId?: number, optionId?: number, categoryId?: number, options?: ApiRequestOptions): Observable<PromptVersion[]> {
        const params: any = {};
        if (productId) params.product_id = productId;
        if (featureId) params.feature_id = featureId;
        if (optionId) params.feature_option_id = optionId;
        if (categoryId) params.category_id = categoryId;

        return this.api.get('/prompts', params, { ...this.defaultOptions, ...options });
    }

    createPrompt(data: PromptVersion): Observable<PromptVersion> {
        return this.api.post('/prompt/add', data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    updatePrompt(id: number, data: PromptVersion): Observable<PromptVersion> {
        return this.api.put(`/prompt/update/${id}`, data, { ...this.defaultOptions, showSuccessToaster: true });
    }

    deletePrompt(id: number): Observable<any> {
        return this.api.delete(`/prompt/delete/${id}`, { ...this.defaultOptions, showSuccessToaster: true });
    }
}
